/**
 * Reviews API client.
 *
 * - Public submission helpers use fetch (no auth) — matches the manifest fetch pattern.
 * - Owner-side helpers use the authenticated axios `api` instance.
 */

import axios from 'axios';
import { getApiBaseUrl } from './api';
import { authHeaders } from './authHeaders';
import type { SubmitReviewPayload, SubmitReviewResponse } from '../components/video-share/review/types';
import type {
  ListReviewsResponse,
  OwnerReviewComment,
  ReviewCommentStatus,
} from '../components/video-workspace/reviews/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface SubmitReviewError {
  status: number;
  error: string;
  message: string;
}

export async function submitReview(shareToken: string, payload: SubmitReviewPayload): Promise<SubmitReviewResponse> {
  const res = await fetch(`${API_BASE_URL}/api/video-share/${shareToken}/reviews`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      // ignore parse errors
    }
    const err: SubmitReviewError = {
      status: res.status,
      error: body?.error ?? 'INTERNAL',
      message: body?.message ?? 'Failed to submit review',
    };
    throw err;
  }

  return (await res.json()) as SubmitReviewResponse;
}

// ============================================================================
// Owner-side (authenticated)
// ============================================================================

async function authedRequest<T>(method: 'get' | 'patch' | 'delete', path: string, data?: unknown): Promise<T> {
  const res = await axios.request<T>({
    method,
    url: `${getApiBaseUrl()}${path}`,
    data,
    headers: await authHeaders(),
  });
  return res.data;
}

export async function listReviewsForConversation(conversationId: string): Promise<ListReviewsResponse> {
  return authedRequest<ListReviewsResponse>('get', `/conversations/${conversationId}/reviews`);
}

export async function patchReviewComment(
  conversationId: string,
  reviewId: string,
  commentId: string,
  update: { status?: ReviewCommentStatus; creatorNote?: string | null; sentMessageId?: string | null },
): Promise<{ success: boolean; comment: OwnerReviewComment }> {
  return authedRequest('patch', `/conversations/${conversationId}/reviews/${reviewId}/comments/${commentId}`, update);
}

export async function deleteReviewById(conversationId: string, reviewId: string): Promise<{ success: boolean }> {
  return authedRequest('delete', `/conversations/${conversationId}/reviews/${reviewId}`);
}
